import Logger from './logger.js';

class EventHandler {
    constructor() {
        this.events = {};
    }

    throttle(func, limit) {
        let lastCall = 0;
        // Viktigt: behåll async-return om func är async
        return async (...args) => {
            const now = Date.now();
            if (now - lastCall >= limit) {
                lastCall = now;
                return await func(...args);
            }
            // Om throttlad bort: returnera undefined direkt
            return undefined;
        };
    }

    add(eventName, callback = null, options = {}) {
        if (typeof eventName !== 'string' || !eventName.trim()) {
            Logger.error('Event name must be a non-empty string');
            return;
        }
        if (!this.events[eventName]) this.events[eventName] = [];

        if (typeof callback !== 'function') {
            Logger.error('Event Callback must be a function');
            return;
        }

        let wrappedCallback = callback;
        if (options.throttle && typeof options.throttle === 'number') {
            wrappedCallback = this.throttle(callback, options.throttle);
        }

        this.events[eventName].push({ callback: wrappedCallback, original: callback, options });
    }


    /**
     * Kör alla listeners och väntar tills de är klara.
     * Returnerar en array av resultat (eller errors om du använder allSettled).
     */
    async trigger(eventName, ...args) {
        const list = this.events[eventName];
        if (!list || !list.length) {
            Logger.debug("No listeners for event: %s", eventName);
            return [];
        }

        // Kör i serie om ordningen spelar roll:
        const results = [];
        for (const entry of [...list]) {
            const { callback, original, options } = entry;
            try {
                const r = callback(...args);
                // vänta på ev. Promise
                results.push(await r);
                if (options?.once) this.remove(eventName, original);
            } catch (err) {
                Logger.error("Error in event handler for %s:", eventName, err);
                results.push(Promise.reject(err));
                // Vill du avbryta kedjan vid fel? kasta vidare här:
                // throw err;
            }
        }
        return results;
    }

    /**
     * True when at least one listener is registered for the event.
     */
    has(eventName) {
        return !!(this.events[eventName] && this.events[eventName].length);
    }

    /**
     * Resolves true as soon as a listener exists for the event, false after timeoutMs.
     * Lets code that runs before the popup modules are built wait for them instead of
     * triggering an event nobody hears yet.
     */
    async waitFor(eventName, timeoutMs = 8000) {
        const start = Date.now();
        while (!this.has(eventName)) {
            if (Date.now() - start > timeoutMs) return false;
            await new Promise(r => setTimeout(r, 50));
        }
        return true;
    }

    /**
     * Synkron variant om du *inte* vill vänta.
     */
    triggerSync(eventName, ...args) {
        const list = this.events[eventName];
        if (!list || !list.length) {
            Logger.debug("No listeners for event: %s", eventName);
            return;
        }
        for (const { callback, original, options } of [...list]) {
            try {
                callback(...args);
                if (options?.once) this.remove(eventName, original);
            } catch (err) {
                Logger.error("Error in event handler for %s:", eventName, err);
            }
        }
    }

    remove(eventName, callback) {
        if (!this.events[eventName]) {
            Logger.debug("No listeners for event: %s", eventName);
            return;
        }
        this.events[eventName] = this.events[eventName].filter(e => e.original !== callback);
        if (!this.events[eventName].length) delete this.events[eventName];
    }

}

const events = new EventHandler();
export default events;